import {inject, Injectable} from '@angular/core';
import {CommunicationCenterService} from "@modules/communication-center";
import {DataEntity, OctopusConnectService} from "octopus-connect";
import {map, mergeMap, take, tap} from "rxjs/operators";
import {Observable, ReplaySubject} from "rxjs";
import {TypedDataCollectionInterface} from "shared/models/octopus-connect";
import {UsageEntity} from "shared/usages.service";
import {AuthenticationService} from "@modules/authentication";


export interface GroupAttributes {
    workgroupname: string,
    groupname: string,
    type?: string,
    archived: false,
    projects?: string[],
    level: string,
    color?: string,
    schoolyear_term:string,
    parents: string[]
}
export interface EducationalLevelAttributesInterface {
    label: string;
    weight: string;
}

export interface GetEducationalLevelOptions {
    filters: {
        label: string;
    } | {
        id: string | number | (string | number)[];
    }
}

export interface Chapter {
    assignations: {[key: string]: {id: string, type: string, assignated_node: string}},
    chapterId: string,
    progress: number,
    progressChapter:  {[key: string]: {score: number, scoreByActivities:  {[key: string]: number}}}[],
    sequences: number[],
    state: string
}

export interface UserProgression {
    chapters : Chapter[];
    completed: number;
    pending: number;
    total: number;
    seancesPercent: number;
}
export type EducationalLevelCollection = TypedDataCollectionInterface<EducationalLevelAttributesInterface>;
@Injectable({
  providedIn: 'root'
})
export class FirstTimeUserExperienceService {
    communicationCenter = inject(CommunicationCenterService);
    octopusConnect = inject(OctopusConnectService);
    authenticationService = inject(AuthenticationService);

    public getEducationalLevels(options?: GetEducationalLevelOptions): Observable<EducationalLevelCollection> {
        return this.octopusConnect.loadCollection('educational_level', options?.filters) as Observable<EducationalLevelCollection>;
    }

    public createGroup(group: GroupAttributes): Observable<DataEntity> {
        const group$ = new ReplaySubject<Observable<DataEntity>>(1);
        if (this.userData.get('sso')) {
            this.communicationCenter
                .getRoom('groups-management')
                .next('add-workgroup', {
                    group: group,
                    callbackSubject: group$
                })
        } else {
            this.communicationCenter
                .getRoom('groups-management')
                .next('add-group', {
                    group: group,
                    callbackSubject: group$
                });
        }

        return group$.pipe(
            take(1),
            mergeMap(obs => obs),
        );
    }

    public loadConcepts(): Observable<DataEntity[]> {
        const subjectConcepts = new ReplaySubject<Observable<DataEntity[]>>(1);
        subjectConcepts.pipe(
            mergeMap((concepts$: Observable<DataEntity[]>) => concepts$),
            take(1)
        ).subscribe();
        this.communicationCenter
            .getRoom('assignment')
            .next('getConcepts$', subjectConcepts);
        return subjectConcepts.pipe(
            mergeMap((concepts$: Observable<DataEntity[]>) => concepts$),
            take(1)
        );
    }

    public loadUserProgression(userProgressFilter: {
        educationalLevel: string,
        concept: string,
    }): Observable<UserProgression> {
        return this.octopusConnect.loadCollection('user-progress', userProgressFilter).pipe(
            map((collection) => collection.entities[0].get('currentUser'))
        );
    }

    public getCollectiveUsage(): Observable<UsageEntity> {
        return this.communicationCenter
            .getRoom('usage')
            .getSubject('collectiveUsage')
            .pipe(take(1));
    }

    get userData(): DataEntity {
        return this.authenticationService.userData;
    }
}
